import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MoveOption, BattleLog, BattleFrame, SessionView } from './simTypes';
import { Slot, BattleField, nameMap, skillIconUrl, skillLabel, slotIndexOf, floatsOf, usedSkillsOf, strikesOf, passivesOf, CritIcon, noteLabel } from './simShared';
import type { SlotInput } from './SimPage';

/**
 * **직접 해보기** — 공덱 몹의 턴이 오면 멈추고, 사람이 게임처럼 스킬을 누르고 대상을 눌러 친다. 방덱은 AI 다.
 *
 * 싸움은 **서버 세션**에서 진행한다(사용자 지시 2026-10-11: 엔진은 서버에서만). 화면은 고른 수를 보내고, 서버가 낸
 * 로그·장면(새로 생긴 것만)을 받아 쌓는다.
 *
 * 화면은 게임 전투 화면을 닮게 했다(사용자 지시 2026-10-11): 위에 상대 방덱, 아래에 우리 공덱, 오른쪽 아래에 스킬 버튼
 * (아이콘·쿨이면 어둡게 남은 턴·봉인·침묵이면 잠금). 스킬을 고르면 칠 수 있는 적(아군 스킬이면 아군)이 빛나고, 누르면 쓴다.
 * 스킬 버튼은 고르기만 하고, **대상을 눌러야 쓴다**(게임처럼, 사용자 지시): 아군에게 거는 스킬(회복·버프·아군 하나)은 아군을,
 * 공격 스킬은 적을(광역은 아무 적이나, 단일은 칠 수 있는 적만). 스킬 이름은 한글이 없어 "1스·2스·3스"로 쓴다.
 * 내가 친 뒤 상대(방덱)가 이어서 움직이는 것도 **한 행동씩 차례로** 재생한다 — 그 몹 발밑 마법진, 쓴 스킬 아이콘, 맞은 몹 위 숫자
 * (사용자 지시: 상대가 턴을 먹어 칠 때도 내가 칠 때처럼). 재생하는 동안은 고를 수 없고 [건너뛰기]로 넘길 수 있다.
 * 지금은 데스크톱에서만 다듬는다(사용자 지시).
 */
/** 한 행동을 보여 주는 시간 (ms) */
const PLAY_MS = 1100;

/** 서버 세션의 화면 쪽 사본 — 로그·장면은 받은 만큼 쌓는다 */
interface Game {
    id: string;
    log: BattleLog[];
    frames: BattleFrame[];
    view: SessionView['view'];
    prompt: SessionView['prompt'];
    result: SessionView['result'];
    names: string[];
}

export default function SimManual({ atk, def, slots, monsterMapping, panel }: {
    atk: SlotInput[]; def: SlotInput[]; slots: Slot[]; monsterMapping: any; panel: React.CSSProperties;
}) {
    const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e6));
    const [g, setG] = useState<Game | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [picked, setPicked] = useState<number | null>(null);
    const [showLog, setShowLog] = useState(false);
    /**
     * 재생 중인 장면들 — 고른 뒤(또는 싸움 시작부터) 서버가 낸 장면을 하나씩 보인다. `from` 은 첫 장면 앞의 로그 길이.
     * 다 보이면 null — 그때는 지금 상태와 마지막 장면의 숫자를 보인다.
     */
    /** before — 첫 장면 앞의 몹 상태(체력이 타마다 깎이는 출발점) */
    const [play, setPlay] = useState<{ frames: BattleFrame[]; idx: number; from: number; before?: BattleFrame['units'] } | null>(null);
    const [step, setStep] = useState(0);
    const logBox = useRef<HTMLDivElement>(null);

    /** 새로 받은 장면(행동한 것만)을 차례로 재생한다 */
    const playFrom = (fr: BattleFrame[], logStart: number, before?: BattleFrame['units']) => {
        const acted = fr.filter((f) => f.actor >= 0);
        if (acted.length) setPlay({ frames: acted, idx: 0, from: logStart, before });
        else setPlay(null);
        setStep((x) => x + 1);
    };
    /** 서버 응답을 사본에 붙인다 */
    const apply = (prev: Game | null, v: SessionView): Game => ({
        id: v.id,
        log: [...(prev && v.logFrom ? prev.log.slice(0, v.logFrom) : []), ...v.log],
        frames: [...(prev && v.frameFrom ? prev.frames.slice(0, v.frameFrom) : []), ...v.frames],
        view: v.view, prompt: v.prompt, result: v.result, names: v.names
    });
    const post = async (url: string, body: any): Promise<SessionView> => {
        const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const d = await r.json();
        if (!d?.success) throw new Error(d?.error || '서버가 답하지 않습니다');
        return d as SessionView;
    };

    const restart = async (sd: number) => {
        setBusy(true); setErr(''); setPlay(null);
        try {
            const v = await post('/api/sim/session', { atk, def, seed: sd });
            const ng = apply(null, v);
            setG(ng);
            setPicked(ng.prompt?.suggest?.slot ?? 1);
            // 싸움 시작부터 방덱이 먼저 움직였으면 그것부터 보인다
            playFrom(ng.frames, 0);
        } catch (e: any) { setErr(e.message); }
        setBusy(false);
    };
    // 덱·주사위가 바뀌면 새 판
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { restart(seed); }, [atk, def, seed]);

    const name = useMemo(() => nameMap(slots, monsterMapping), [slots, monsterMapping]);
    useEffect(() => { if (logBox.current) logBox.current.scrollTop = logBox.current.scrollHeight; });

    // 재생 — 한 장면씩 넘긴다
    useEffect(() => {
        if (!play || !g) return;
        // 협공·반격으로 여럿이 차례로 달려가면 그만큼 더 보여 준다
        const f = play.frames[play.idx];
        const from = play.idx > 0 ? play.frames[play.idx - 1].logEnd : play.from;
        const st = strikesOf(g.log.slice(from, f.logEnd), slots, f.actor);
        const extra = Math.max(0, st.total * 1000 + 450 - PLAY_MS);
        const t = window.setTimeout(() => {
            if (play.idx + 1 < play.frames.length) { setPlay({ ...play, idx: play.idx + 1 }); setStep((x) => x + 1); }
            else setPlay(null);
        }, PLAY_MS + extra);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [play]);

    /** 지금 보일 장면 — 재생 중이면 그 장면, 아니면 지금 상태 */
    const shown = play ? play.frames[play.idx] : null;
    const shownFrom = play ? (play.idx > 0 ? play.frames[play.idx - 1].logEnd : play.from) : 0;
    const recent: BattleLog[] = g && shown ? g.log.slice(shownFrom, shown.logEnd) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const floats = useMemo(() => floatsOf(recent, slots), [step]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const used = useMemo(() => usedSkillsOf(recent, slots, shown?.actor ?? -1), [step]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const strikes = useMemo(() => strikesOf(recent, slots, shown?.actor ?? -1), [step]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const passives = useMemo(() => passivesOf(recent, slots), [step]);

    if (!g) return err ? <div style={{ ...panel, color: '#d32f2f', fontWeight: 700 }}>{err}</div> : null;
    const playing = !!play;
    const view = shown ? shown.units : g.view;
    // 재생하는 동안·서버를 기다리는 동안은 고르지 않는다
    const prompt = playing || busy ? null : g.prompt;
    const opt: MoveOption | undefined = prompt?.options.find((o) => o.slot === picked);
    /** 아군에게 거는 스킬(회복·버프·아군 하나)이면 아군을, 공격 스킬이면 적을 눌러야 쓴다 */
    const needAlly = !!opt && opt.usable && (opt.allyTarget || opt.utility);
    const needTarget = !!opt && opt.usable && !needAlly;

    /** 서버에 한 수를 보내고, 그 뒤 새로 생긴 장면(내 행동 + 상대 행동들)을 차례로 재생한다 */
    const send = async (body: any, replay = true) => {
        if (busy) return;
        setBusy(true); setErr('');
        try {
            const l0 = g.log.length;
            const v = await post(`/api/sim/session/${g.id}/move`, { ...body, logFrom: l0, frameFrom: g.frames.length });
            const ng = apply(g, v);
            setG(ng);
            setPicked(ng.prompt?.suggest?.slot ?? 1);
            // 재생 첫 장면은 앞 장면 끝부터 — 내 몹 턴 시작에 터진 패시브(불하그 턴 시작 회복 등)는 고르기 전에 이미 로그에 있어서,
            // 이번 수의 로그(l0)부터 자르면 빠졌다 (사용자 2026-10-22 "불하그는 왜 패시브가 안뜨지?")
            const lastEnd = g.frames.length ? g.frames[g.frames.length - 1].logEnd : 0;
            if (replay) playFrom(v.frames, Math.min(l0, lastEnd), g.view);
            else { setPlay(null); setStep((x) => x + 1); }
        } catch (e: any) { setErr(e.message); }
        setBusy(false);
    };
    const go = (slot: number, target: number) => send({ slot, target });
    const byAi = () => send({});
    /** AI 로 끝까지 — 장면이 많아 재생하지 않고 결과만 */
    const toEnd = () => send({ auto: true }, false);
    const skip = () => { setPlay(null); setStep((x) => x + 1); };
    /** 스킬 버튼 — 고르기만 한다. 쓰려면 대상을 눌러야 한다 */
    const pressSkill = (o: MoveOption) => {
        if (!o.usable || !prompt) return;
        setPicked(o.slot);
    };

    const actorIdx = shown ? shown.actor : prompt?.actor ?? -1;
    /** 누를 수 있는 몹 — 아군 스킬은 살아 있는 아군, 광역 공격은 살아 있는 적 아무나, 단일 공격은 칠 수 있는 적(도발·끌기 반영) */
    const canPick = (i: number) => !!prompt && !!opt && (
        opt.either && opt.usable ? prompt.allies.includes(i) || prompt.targets.includes(i)
        : needAlly ? prompt.allies.includes(i)
            : needTarget ? (opt.aoe ? i >= 3 && !view[i].dead : prompt.targets.includes(i)) : false
    );

    const small: React.CSSProperties = {
        border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: '#e8ddc8', fontWeight: 800,
        fontSize: 12, padding: '5px 10px', cursor: 'pointer'
    };
    const actorSlot = prompt ? slots[prompt.actor] : undefined;

    return (
        <div style={{ ...panel, background: 'radial-gradient(ellipse at 50% 35%, #34485e 0%, #1c2633 70%)', color: '#eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 900, color: '#f6d77a' }}>직접 플레이</span>
                <span style={{ flex: 1 }} />
                {/* 처음부터 — 같은 덱으로 새로 굴린다 */}
                <button type="button" style={small} onClick={() => setSeed(Math.floor(Math.random() * 1e6))}>처음부터</button>
                <button type="button" style={small} onClick={byAi} disabled={!prompt || playing || busy}>AI 1회</button>
                <button type="button" style={small} onClick={toEnd} disabled={!!g.result || busy} title="AI 로 끝까지">AI</button>
                <button type="button" style={small} onClick={() => setShowLog((x) => !x)}>{showLog ? '기록 닫기' : '기록 보기'}</button>
            </div>

            {err && <div style={{ color: '#ff8a80', fontSize: 12, fontWeight: 800, marginBottom: 6 }}>{err}</div>}
            {/* 전장 — 게임 전투 화면처럼 경기장 배경, 위 방덱 · 아래 공덱 */}
            <div style={{ position: 'relative' }}>
                <BattleField
                    slots={slots} units={view} monsterMapping={monsterMapping} acting={actorIdx}
                    floats={floats} floatKey={step} used={used} strikes={strikes} passives={passives}
                    prevUnits={play ? (play.idx > 0 ? play.frames[play.idx - 1].units : play.before) : undefined}
                    selectable={(i) => !g.result && canPick(i)} onPick={(i) => opt && go(opt.slot, i)}
                    aimed={prompt?.suggest && picked === prompt.suggest.slot && canPick(prompt.suggest.target) ? prompt.suggest.target : -1}
                />
                {playing && shown && (
                    <div style={{
                        position: 'absolute', left: '50%', top: 8, transform: 'translateX(-50%)', fontSize: 12, fontWeight: 900, zIndex: 6,
                        color: shown.actor >= 3 ? '#ffb4a8' : '#b9e3ff', background: 'rgba(0,0,0,0.55)', borderRadius: 999, padding: '4px 12px', whiteSpace: 'nowrap'
                    }}>
                        {shown.actor >= 3 ? '상대 턴' : '내 턴'} · {name(g.names[shown.actor])}
                        {used[shown.actor] ? ` · ${used[shown.actor]!.name}` : ''}
                    </div>
                )}
                {playing && (
                    <button type="button" onClick={skip} style={{ ...small, position: 'absolute', right: 10, bottom: 10, zIndex: 6, background: 'rgba(0,0,0,0.55)' }}>건너뛰기 ▶▶</button>
                )}
                {prompt?.suggest && picked === prompt.suggest.slot && !g.result && (
                    <div style={{ position: 'absolute', right: 10, top: 8, fontSize: 11, fontWeight: 800, color: '#ffd54f', background: 'rgba(0,0,0,0.5)', borderRadius: 8, padding: '3px 8px', zIndex: 6 }}>
                        ★ AI 추천 대상: {name(g.names[prompt.suggest.target])}
                    </div>
                )}
                {/* 스킬 버튼 — 게임처럼 전장 오른쪽 아래 */}
                {prompt && !playing && !g.result && (
                    <div style={{ position: 'absolute', right: 12, bottom: 10, display: 'flex', gap: 10, zIndex: 20 }}>
                        {prompt.options.map((o) => {
                            const sk = actorSlot?.unit?.skills?.find((k) => k.slot === o.slot);
                            return (
                                <SkillButton
                                    key={o.slot} o={o} iconId={sk?.id ?? null} on={picked === o.slot}
                                    suggest={prompt.suggest?.slot === o.slot}
                                    onPress={() => pressSkill(o)}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {g.result && !playing && (
                <div style={{ marginTop: 14, textAlign: 'center', fontSize: 26, fontWeight: 900, letterSpacing: 1, color: g.result.win ? '#8ec5ff' : '#ff8a80', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                    {g.result.win ? 'VICTORY' : 'DEFEAT'}
                    <div style={{ fontSize: 13, color: '#c9d4e0', fontWeight: 700, letterSpacing: 0 }}>{g.result.win ? '공덱 승' : '방덱 승'} · {g.result.turns}턴</div>
                </div>
            )}
            {/* 기록 */}
            {(showLog || (g.result && !playing)) && (
                <div ref={logBox} style={{ marginTop: 12, height: 220, overflowY: 'auto', background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: '8px 10px' }}>
                    {g.log.map((l, k) => {
                        const who = slotIndexOf(slots, l.actor);
                        return (
                            <div key={k} style={{ fontSize: 12, color: '#dfe6ee', fontWeight: 600, lineHeight: 1.6 }}>
                                <span style={{ color: '#7f8ea0', marginRight: 6 }}>{l.turn}</span>
                                <b>{name(l.actor)}</b> {skillLabel(slots[who], l.skill, l.note)}
                                {l.target && <> → <b>{l.target === '(아군)' ? '아군' : name(l.target)}</b></>}
                                {l.damage ? <span style={{ color: '#ff5a4f', fontWeight: 900 }}> {l.crit && <CritIcon size={15} />}{l.damage.toLocaleString()}{l.glancing ? ' 빗맞음' : ''}</span> : null}
                                {noteLabel(l.skill, l.note) && <span style={{ color: '#8d9bab' }}> · {noteLabel(l.skill, l.note)}</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/**
 * **스킬 버튼 하나** — 게임처럼 아이콘 네모. 고르면 금빛 테두리, 쿨이면 어둡게 남은 턴, 못 쓰면(봉인·침묵) 잠금,
 * 아래에 "1스", AI 추천이면 작은 별.
 */
function SkillButton({ o, iconId, on, suggest, onPress }: {
    o: MoveOption; iconId: number | null; on: boolean; suggest: boolean; onPress: () => void;
}) {
    const size = 60;
    const locked = !o.usable && !(o.cooldown > 0);
    return (
        <div
            onClick={onPress} title={o.why}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: o.usable ? 'pointer' : 'not-allowed' }}
        >
            <div style={{
                position: 'relative', width: size, height: size, borderRadius: 12, overflow: 'hidden', boxSizing: 'border-box',
                border: on ? '3px solid #ffd54f' : '3px solid #6b5a3a', background: '#111',
                boxShadow: on ? '0 0 14px rgba(255,213,79,0.85), 0 3px 8px rgba(0,0,0,0.5)' : '0 3px 8px rgba(0,0,0,0.5)',
                transform: on ? 'translateY(-4px)' : 'none', transition: 'transform 0.15s, box-shadow 0.15s'
            }}>
                {iconId
                    ? <img src={skillIconUrl(iconId)} alt={`${o.slot}스`} width={size} height={size} draggable={false}
                        style={{ display: 'block', width: '100%', height: '100%', filter: o.usable ? 'none' : 'grayscale(0.8) brightness(0.55)' }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                    : null}
                {o.cooldown > 0 && (
                    <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 26, fontWeight: 900, textShadow: '0 2px 4px #000'
                    }}>{o.cooldown}</div>
                )}
                {locked && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }} title={o.why}>🔒</div>
                )}
                {suggest && o.usable && (
                    <span title="AI 추천" style={{ position: 'absolute', right: 3, top: 1, fontSize: 13, color: '#ffd54f', textShadow: '0 0 3px #000' }}>★</span>
                )}
            </div>
            <span style={{ fontSize: 13, fontWeight: 900, color: on ? '#ffd54f' : '#dfe8f2', textShadow: '0 1px 3px #000' }}>{o.slot}스</span>
        </div>
    );
}
